// content/sceneobjects/puppy/skinned_renderpipeline.ts

import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import Camera from "../../scene/camera";
import Mesh from "../../geometry/mesh";
import ResourceService from "../../resource/resourceservice";
import Transform from "../transform";
import { ModelData } from "../../geometry/modeldata";
import { makeSkinnedMeshDataFromPrimitive } from "../../geometry/meshdata_factory";
import AnimationControllerGpu from "../../animation/animation_controller_gpu";

export default class SkinnedRenderPipeline extends RenderPipeline {
  label: string;
  shader_name: string;
  texture_name: string;

  camera_buffer: GPUBuffer | null = null;
  object_buffer: GPUBuffer | null = null;
  paper_buffer: GPUBuffer | null = null;

  texture_bind_group: GPUBindGroup | null = null;
  per_frame_bind_group: GPUBindGroup | null = null;

  mesh: Mesh;

  model: ModelData | null = null;
  skin_index: number = -1;
  mesh_node_index: number = -1;

  // Held between prepare() and finish_draw() so the lease lifetime is
  // exactly one draw call.
  private palette_owner: AnimationControllerGpu | null = null;

  constructor(key: string, shader_name: string, texture_name: string) {
    super(key + "_skinned");
    this.label = key + "_skinned";
    this.shader_name = shader_name;
    this.texture_name = texture_name + ".png";
    this.mesh = new Mesh();
  }

  async buildFromModel(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService,
    model: ModelData
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      label: this.label,
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 64,
            attributes: [
              { shaderLocation: 0, offset: 0,  format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x2" },
              { shaderLocation: 3, offset: 32, format: "uint32x4" },
              { shaderLocation: 4, offset: 48, format: "float32x4" },
            ],
          },
        ],
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format,
            blend: {
              color: {
                operation: "add",
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
              },
              alpha: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
              },
            },
          },
        ],
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none",
      },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: true,
        depthCompare: "less",
      },
    });

    this.camera_buffer = device.createBuffer({
      size: 80,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.object_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.paper_buffer = device.createBuffer({
      size: 48,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const texture_resource = await resource_service.getTexture(this.texture_name);
    const texture_asset = texture_resource.value;

    this.texture_bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(1),
      label: this.label + "_tex_bind_group",
      entries: [
        { binding: 0, resource: texture_asset.sampler },
        { binding: 1, resource: texture_asset.view },
      ],
    });

    if (!model.meshes.length) {
      throw new Error("Skinned model contains no mesh primitives");
    }

    const primitive = model.meshes[0];
    const skin_index = primitive.skinIndex;

    if (skin_index === undefined) {
      throw new Error("Skinned primitive is not associated with a skin");
    }

    this.model = model;
    this.skin_index = skin_index;
    this.mesh_node_index = primitive.nodeIndex;

    const mesh_data = makeSkinnedMeshDataFromPrimitive(primitive);
    this.mesh.build(device, mesh_data);
  }

  prepare(
    device: GPUDevice,
    camera: Camera,
    transform: Transform,
    animation_controller_gpu: AnimationControllerGpu
  ): void {
    if (
      !this.camera_buffer ||
      !this.object_buffer ||
      !this.paper_buffer ||
      !this.pipeline ||
      !this.model
    ) {
      return;
    }

    const cameraData = new Float32Array(20);
    cameraData.set(camera.view_projection_matrix, 0);
    cameraData[16] = camera.eye[0];
    cameraData[17] = camera.eye[1];
    cameraData[18] = camera.eye[2];
    cameraData[19] = 0.0;
    device.queue.writeBuffer(this.camera_buffer, 0, cameraData);

    const paperData = new Float32Array([
      -0.20, 0.78, 0.42, 0.10,
       1.00, 0.985, 0.95, 0.075,
       0.14, 0.52, 0.92, 0.0,
    ]);
    device.queue.writeBuffer(this.paper_buffer, 0, paperData);

    device.queue.writeBuffer(
      this.object_buffer,
      0,
      transform.model_matrix.buffer as ArrayBuffer,
      transform.model_matrix.byteOffset,
      transform.model_matrix.byteLength
    );

    const palette_buffer = animation_controller_gpu.acquire_latest_joint_palette();
    if (!palette_buffer) {
      this.per_frame_bind_group = null;
      this.palette_owner = null;
      return;
    }

    this.palette_owner = animation_controller_gpu;

    this.per_frame_bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label + "_cam_obj_joint_bind_group",
      entries: [
        { binding: 0, resource: { buffer: this.camera_buffer } },
        { binding: 1, resource: { buffer: this.object_buffer } },
        { binding: 2, resource: { buffer: palette_buffer } },
        { binding: 3, resource: { buffer: this.paper_buffer } },
      ],
    });
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.per_frame_bind_group || !this.texture_bind_group) return;

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.per_frame_bind_group);
    pass.setBindGroup(1, this.texture_bind_group);
    this.mesh.draw(pass);
  }

    finish_draw(): void {
    if (this.palette_owner) {
      this.palette_owner.release_latest_joint_palette();
      this.palette_owner = null;
    }
    this.per_frame_bind_group = null;
  }
}